// A/B Test Step Component
import { DeviceThermostat } from '@mui/icons-material';
import { FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import {
    setABTestConfig,
    addSend,
    resetSends,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { ABTestConfig, ABTestType } from 'domains/campaigns/types';
import FormLayout from 'domains/core/components/FormLayout';

type Props = {
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setActiveStep: (step: CustomCampaignSteps) => void;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignSetupABTestStep = ({
    campaignState,
    dispatchCampaignAction,
    setActiveStep,
    setIsContinueEnabled,
    setNextStep,
}: Props) => {
    const currentABTestConfig = campaignState.abTestConfig;
    const [selectedTestType, setSelectedTestType] = useState<ABTestType>(currentABTestConfig?.testOn);

    useEffect(() => {
        // Set next step - both content and send time A/B testing go to SELECT_CONTENT
        // The router will determine whether to show shared content selection or individual content selection
        setNextStep(CustomCampaignSteps.SELECT_CONTENT);

        // Ensure we have two sends if A/B testing is already selected
        if (
            (currentABTestConfig?.testOn === ABTestType.CONTENT ||
                currentABTestConfig?.testOn === ABTestType.SEND_TIME) &&
            campaignState.sends.length === 1
        ) {
            dispatchCampaignAction(addSend(0));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentABTestConfig?.testOn]);

    useEffect(() => {
        // Only enable continue if a valid test type is selected
        const isValidTestTypeSelected =
            selectedTestType === ABTestType.CONTENT || selectedTestType === ABTestType.SEND_TIME;
        setIsContinueEnabled(isValidTestTypeSelected);
    }, [selectedTestType, setIsContinueEnabled]);

    const handleTestTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newTestType = event.target.value as ABTestType;
        setSelectedTestType(newTestType);

        // Preserve existing testPercentage value from previous step, default to 50 if not set
        const existingTestPercentage = currentABTestConfig?.testPercentage || 50;

        const newAbTestConfig: ABTestConfig = {
            testOn: newTestType,
            testPercentage: existingTestPercentage,
        };

        dispatchCampaignAction(setABTestConfig(newAbTestConfig));

        // For both content and send time A/B testing, reset sends to initial state then add second send
        if (newTestType === ABTestType.CONTENT || newTestType === ABTestType.SEND_TIME) {
            dispatchCampaignAction(resetSends()); // Reset sends to initial state
            dispatchCampaignAction(addSend(0)); // Add second send with parentIndex 0
        }
    };

    return (
        <FormLayout
            header="set up a/b test"
            stepName="a/b test"
            icon={<DeviceThermostat />}
            guidingText="Set up your test by selecting what you'd like to compare—subject line, content, or send time. See what resonates best with your audience."
        >
            <Stack spacing={3}>
                <Typography variant="body1">What do you want to test?</Typography>

                <RadioGroup value={selectedTestType} onChange={handleTestTypeChange}>
                    <Stack spacing={2}>
                        <FormControlLabel
                            value={ABTestType.CONTENT}
                            control={<Radio size="small" color="secondary" />}
                            label={
                                <Stack spacing={0.5}>
                                    <Typography variant="body1">Content</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        subject line, preview text, body or sender profile
                                    </Typography>
                                </Stack>
                            }
                        />

                        <FormControlLabel
                            value={ABTestType.SEND_TIME}
                            control={<Radio size="small" color="secondary" />}
                            label={
                                <Stack spacing={0.5}>
                                    <Typography variant="body1">Send time</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        send date and time
                                    </Typography>
                                </Stack>
                            }
                        />
                    </Stack>
                </RadioGroup>
            </Stack>
        </FormLayout>
    );
};

export default CustomCampaignSetupABTestStep;
